import React, { useEffect, useState, useRef, Component } from 'react';
import { ChevronDownIcon, PlusIcon, ChevronLeftIcon } from 'lucide-react';
interface PaymentMethodsFiltersProps {
  paymentMethodCategories: string[];
  onPaymentMethodCategoriesChange: (categories: string[]) => void;
  paymentMethods: string[];
  onPaymentMethodsChange: (methods: string[]) => void;
  cardBrands: string[];
  onCardBrandsChange: (brands: string[]) => void;
  fundingSourceTypes: string[];
  onFundingSourceTypesChange: (types: string[]) => void;
  cardTypes: string[];
  onCardTypesChange: (types: string[]) => void;
  inputMethods: string[];
  onInputMethodsChange: (methods: string[]) => void;
  interactionTypes: string[];
  onInteractionTypesChange: (types: string[]) => void;
  networkTokenStatuses: string[];
  onNetworkTokenStatusesChange: (statuses: string[]) => void;
}
export function PaymentMethodsFilters({
  paymentMethodCategories,
  onPaymentMethodCategoriesChange,
  paymentMethods,
  onPaymentMethodsChange,
  cardBrands,
  onCardBrandsChange,
  fundingSourceTypes,
  onFundingSourceTypesChange,
  cardTypes,
  onCardTypesChange,
  inputMethods,
  onInputMethodsChange,
  interactionTypes,
  onInteractionTypesChange,
  networkTokenStatuses,
  onNetworkTokenStatusesChange
}: PaymentMethodsFiltersProps) {
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showMethodDropdown, setShowMethodDropdown] = useState(false);
  const [showCardBrandDropdown, setShowCardBrandDropdown] = useState(false);
  const [showFundingSourceDropdown, setShowFundingSourceDropdown] = useState(false);
  const [showCardTypeDropdown, setShowCardTypeDropdown] = useState(false);
  const [showInputMethodDropdown, setShowInputMethodDropdown] = useState(false);
  const [showInteractionTypeDropdown, setShowInteractionTypeDropdown] = useState(false);
  const [showNetworkTokenDropdown, setShowNetworkTokenDropdown] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);
  const methodRef = useRef<HTMLDivElement>(null);
  const cardBrandRef = useRef<HTMLDivElement>(null);
  const fundingSourceRef = useRef<HTMLDivElement>(null);
  const cardTypeRef = useRef<HTMLDivElement>(null);
  const inputMethodRef = useRef<HTMLDivElement>(null);
  const interactionTypeRef = useRef<HTMLDivElement>(null);
  const networkTokenRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
      if (methodRef.current && !methodRef.current.contains(event.target as Node)) {
        setShowMethodDropdown(false);
      }
      if (cardBrandRef.current && !cardBrandRef.current.contains(event.target as Node)) {
        setShowCardBrandDropdown(false);
      }
      if (fundingSourceRef.current && !fundingSourceRef.current.contains(event.target as Node)) {
        setShowFundingSourceDropdown(false);
      }
      if (cardTypeRef.current && !cardTypeRef.current.contains(event.target as Node)) {
        setShowCardTypeDropdown(false);
      }
      if (inputMethodRef.current && !inputMethodRef.current.contains(event.target as Node)) {
        setShowInputMethodDropdown(false);
      }
      if (interactionTypeRef.current && !interactionTypeRef.current.contains(event.target as Node)) {
        setShowInteractionTypeDropdown(false);
      }
      if (networkTokenRef.current && !networkTokenRef.current.contains(event.target as Node)) {
        setShowNetworkTokenDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <>
      {/* Main Filters - Same row as date/granularity */}
      <div className="relative" ref={categoryRef} data-id="element-1056">
        <button onClick={() => setShowCategoryDropdown(!showCategoryDropdown)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-1057">
          <span data-id="element-1058">Payment method category</span>
          {paymentMethodCategories.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-1059">
              {paymentMethodCategories.length}
            </span>}
          <ChevronDownIcon size={16} data-id="element-1060" />
        </button>

        {showCategoryDropdown && <CompactDropdown title="Filter by: Category" options={['Cards', 'Wallets', 'Bank transfers', 'Buy now pay later', 'Cash-based', 'Direct debit']} selectedOptions={paymentMethodCategories} onApply={selected => {
        onPaymentMethodCategoriesChange(selected);
        setShowCategoryDropdown(false);
      }} onBack={null} data-id="element-1061" />}
      </div>

      <div className="relative" ref={methodRef} data-id="element-1062">
        <button onClick={() => setShowMethodDropdown(!showMethodDropdown)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-1063">
          <span data-id="element-1064">Payment method</span>
          {paymentMethods.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-1065">
              {paymentMethods.length}
            </span>}
          <ChevronDownIcon size={16} data-id="element-1066" />
        </button>

        {showMethodDropdown && <CompactDropdown title="Filter by: Payment method" options={['Card', 'Apple Pay', 'Google Pay', 'Alipay', 'WeChat Pay', 'GrabPay', 'LINE Pay', 'TrueMoney', 'PromptPay', 'PayPal']} selectedOptions={paymentMethods} onApply={selected => {
        onPaymentMethodsChange(selected);
        setShowMethodDropdown(false);
      }} onBack={null} data-id="element-1067" />}
      </div>

      {/* Secondary Filters - Separate row with smaller buttons */}
      <div className="w-full flex flex-wrap items-center gap-2 mt-3" data-id="element-1068">
        <div className="relative" ref={cardBrandRef} data-id="element-1069">
          <button onClick={() => setShowCardBrandDropdown(!showCardBrandDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1070">
            <PlusIcon size={14} data-id="element-1071" />
            <span data-id="element-1072">Card brand</span>
            {cardBrands.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-1073">
                {cardBrands.length}
              </span>}
          </button>

          {showCardBrandDropdown && <CompactDropdown title="Filter by: Card brand" options={['Visa', 'Mastercard', 'American Express', 'Cartes Bancaires', 'Discover', 'eftpos Australia', 'Girocard', 'JCB', 'UnionPay']} selectedOptions={cardBrands} onApply={selected => {
          onCardBrandsChange(selected);
          setShowCardBrandDropdown(false);
        }} onBack={null} data-id="element-1074" />}
        </div>

        <div className="relative" ref={fundingSourceRef} data-id="element-1075">
          <button onClick={() => setShowFundingSourceDropdown(!showFundingSourceDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1076">
            <PlusIcon size={14} data-id="element-1077" />
            <span data-id="element-1078">Funding source type</span>
            {fundingSourceTypes.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-1079">
                {fundingSourceTypes.length}
              </span>}
          </button>

          {showFundingSourceDropdown && <CompactDropdown title="Filter by: Funding source" options={['Card', 'Bank account', 'Stored balance', 'BNPL credit']} selectedOptions={fundingSourceTypes} onApply={selected => {
          onFundingSourceTypesChange(selected);
          setShowFundingSourceDropdown(false);
        }} onBack={null} data-id="element-1080" />}
        </div>

        <div className="relative" ref={cardTypeRef} data-id="element-1081">
          <button onClick={() => setShowCardTypeDropdown(!showCardTypeDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1082">
            <PlusIcon size={14} data-id="element-1083" />
            <span data-id="element-1084">Card type</span>
            {cardTypes.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-1085">
                {cardTypes.length}
              </span>}
          </button>

          {showCardTypeDropdown && <CompactDropdown title="Filter by: Card type" options={['Credit card', 'Debit card', 'Prepaid card']} selectedOptions={cardTypes} onApply={selected => {
          onCardTypesChange(selected);
          setShowCardTypeDropdown(false);
        }} onBack={null} data-id="element-1086" />}
        </div>

        <div className="relative" ref={inputMethodRef} data-id="element-1087">
          <button onClick={() => setShowInputMethodDropdown(!showInputMethodDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1088">
            <PlusIcon size={14} data-id="element-1089" />
            <span data-id="element-1090">Input method</span>
            {inputMethods.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-1091">
                {inputMethods.length}
              </span>}
          </button>

          {showInputMethodDropdown && <CompactDropdown title="Filter by: Input method" options={['In-person (card present)', 'Online (card-not-present)', 'Apple Pay', 'Google Pay']} selectedOptions={inputMethods} onApply={selected => {
          onInputMethodsChange(selected);
          setShowInputMethodDropdown(false);
        }} onBack={null} data-id="element-1092" />}
        </div>

        <div className="relative" ref={interactionTypeRef} data-id="element-1093">
          <button onClick={() => setShowInteractionTypeDropdown(!showInteractionTypeDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1094">
            <PlusIcon size={14} data-id="element-1095" />
            <span data-id="element-1096">Interaction type</span>
            {interactionTypes.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-1097">
                {interactionTypes.length}
              </span>}
          </button>

          {showInteractionTypeDropdown && <CompactDropdown title="Filter by: Interaction type" options={['One time', 'Saved card', 'Recurring initial', 'Recurring subsequent', 'Unscheduled']} selectedOptions={interactionTypes} onApply={selected => {
          onInteractionTypesChange(selected);
          setShowInteractionTypeDropdown(false);
        }} onBack={null} data-id="element-1098" />}
        </div>

        <div className="relative" ref={networkTokenRef} data-id="element-1099">
          <button onClick={() => setShowNetworkTokenDropdown(!showNetworkTokenDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1100">
            <PlusIcon size={14} data-id="element-1101" />
            <span data-id="element-1102">Network token status</span>
            {networkTokenStatuses.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-1103">
                {networkTokenStatuses.length}
              </span>}
          </button>

          {showNetworkTokenDropdown && <CompactDropdown title="Filter by: Network token status" options={['Non-tokenized card', 'Network token']} selectedOptions={networkTokenStatuses} onApply={selected => {
          onNetworkTokenStatusesChange(selected);
          setShowNetworkTokenDropdown(false);
        }} onBack={null} data-id="element-1104" />}
        </div>
      </div>
    </>;
}
// Compact Dropdown Component (for checkbox lists)
interface CompactDropdownProps {
  title: string;
  options: string[];
  selectedOptions: string[];
  onApply: (selected: string[]) => void;
  onBack: (() => void) | null;
  isInline?: boolean;
}
function CompactDropdown({
  title,
  options,
  selectedOptions,
  onApply,
  onBack,
  isInline = false
}: CompactDropdownProps) {
  const [tempSelected, setTempSelected] = useState<string[]>(selectedOptions);
  const toggleOption = (option: string) => {
    if (tempSelected.includes(option)) {
      setTempSelected(tempSelected.filter(o => o !== option));
    } else {
      setTempSelected([...tempSelected, option]);
    }
  };
  const content = <>
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3" data-id="element-1105">
        {onBack && <button onClick={onBack} className="p-1 hover:bg-gray-100 rounded" data-id="element-1106">
            <ChevronLeftIcon size={18} className="text-gray-600" data-id="element-1107" />
          </button>}
        <h3 className="text-base font-semibold text-gray-900" data-id="element-1108">{title}</h3>
      </div>
      <div className="flex-1 overflow-y-auto py-1" data-id="element-1109">
        {options.map(option => <label key={option} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer" data-id="element-1110">
            <input type="checkbox" checked={tempSelected.includes(option)} onChange={() => toggleOption(option)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1111" />
            <span className="text-base text-gray-900" data-id="element-1112">{option}</span>
          </label>)}
      </div>
      <div className="px-4 py-3 border-t border-gray-200" data-id="element-1113">
        <button onClick={() => onApply(tempSelected)} className="w-full px-4 py-2.5 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg" data-id="element-1114">
          Apply
        </button>
      </div>
    </>;
  if (isInline) {
    return <div className="flex flex-col max-h-96" data-id="element-1115">{content}</div>;
  }
  return <div className="absolute left-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 flex flex-col" data-id="element-1116">
      {content}
    </div>;
}